import { Table, TableSummation } from '@/components_deprecated'
import type { DynamicTableSettingsProps } from './types'
import type { DynamicColumnNode } from '@/modules/dynamic_tables'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { dynamicColumnLabel } from './column-label'
import type { DynamicColumnForm } from './EditDynamicColumnModal'
import { EditDynamicColumnModal } from './EditDynamicColumnModal'

export const DynamicTablePreview: React.FC<DynamicTableSettingsProps> = ({
  columns = [],
  forExport,
  onEdit,
  minEnabledColumns = 2,
  lockedColumns,
  labels,
}) => {
  const translations = useTranslate({
    changeName: 'commmon.edit-name',
    preview: 'settings.tables.preview',
    exportPreview: 'settings.export-tables.preview',
    emptyTitle: 'settings.tables.empty-preview-title',
    emptyDescription: 'settings.tables.empty-preview-description',
    error: {
      minimumColumns: 'settings.show-dynamic-column-error',
    },
  })

  const addToast = useToast()
  const addPrompt = usePrompt()

  const enabledColumns = columns.filter(column =>
    forExport ? column.inExport : column.enabled
  )

  async function handleEdit(column: DynamicColumnNode) {
    const lockedValue = lockedColumns?.get(column.name)
    const isLocked = lockedValue !== undefined
    const { data } = await addPrompt<DynamicColumnForm | null>(resolve => (
      <EditDynamicColumnModal
        column={column}
        defaultDisplayName={labels?.get(column.name)}
        disableToggle={isLocked || enabledColumns.length <= minEnabledColumns}
        lockedByModule={isLocked}
        lockedValue={lockedValue}
        onSubmit={resolve}
      />
    ))
    if (data === null) return
    const { displayName, enabled } = data

    if (displayName !== column.displayName || enabled !== column.enabled) {
      onEdit?.(column.id, { displayName, enabled })
    }
  }

  function disableColumnExport(column: DynamicColumnNode) {
    if (enabledColumns.length <= minEnabledColumns) {
      addToast('error', translations.error.minimumColumns)
      return
    }
    onEdit?.(column.id, { inExport: false })
  }

  const columnIcon = forExport ? (
    <Icon icon="times" cursor="pointer" color="error" margin="0 0 0 8px" />
  ) : (
    <Icon icon="edit" cursor="pointer" color="secondary" margin="0 0 0 8px" />
  )

  const preview = forExport ? translations.exportPreview : translations.preview

  if (enabledColumns.length === 0) {
    return (
      <div className="[&>div]:mt-4">
        <h4>{preview}</h4>
        <div className="rounded-lg border border-dashed border-gray-300 px-6 py-8 text-center text-gray-500">
          <strong className="mb-2 block text-gray-900">
            {translations.emptyTitle}
          </strong>
          {translations.emptyDescription}
        </div>
      </div>
    )
  }

  return (
    <div className="[&>div]:mt-4">
      <h4>{preview}</h4>
      <Table headerMargin rowHoverBackground="gray10">
        <thead>
          <tr>
            {enabledColumns.map(column => {
              const disabled = column.name === 'date'
              return (
                <th
                  key={column.name}
                  className={`group !pr-0 ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  } first:[&>div]:mr-2`}
                  onClick={() =>
                    !disabled &&
                    (forExport
                      ? disableColumnExport(column)
                      : handleEdit(column))
                  }
                >
                  <div className="flex items-center">
                    <span className="truncate">
                      {dynamicColumnLabel(column, labels)}
                    </span>
                    {!disabled && (
                      <span className="ml-2 inline-flex shrink-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        {columnIcon}
                      </span>
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          <TableSummation className="h-[5.5rem] hover:!bg-inherit [&>td]:!pt-4 max-md:[&>td:first-of-type>div]:!ml-4 max-md:[&>td:last-of-type>div]:!mr-4">
            {enabledColumns.map(column =>
              column.columnType === 'TIME' ? (
                <td key={'sum-' + column.name}>
                  <div>
                    <span>{dynamicColumnLabel(column, labels)}</span>
                    00:00
                  </div>
                </td>
              ) : (
                <td key={'sum-' + column.name}>
                  <div />
                </td>
              )
            )}
          </TableSummation>
        </tbody>
      </Table>
    </div>
  )
}
